import { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiGet, apiDelete, authedFetch, API_BASE } from '@/lib/api';

/**
 * Bulk Worksheets — the admin side, deliberately built like Bulk Infographics.
 *
 * Pick the board / class / subject / chapter once, drop in as many worksheet
 * files as you like, and each one is published against that chapter. Students
 * on that board and class see them immediately, the same way infographics and
 * cinegraphics appear.
 */
const LEVELS = ['basic', 'standard', 'challenge'];

export function ContentMgmtWorksheetsBulk() {
  const [scope, setScope] = useState({ board: 'cbse', class_level: '', subject: '', chapter: '', level: 'standard' });
  const [files, setFiles] = useState([]);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await apiGet('/admin/worksheets');
      setRows(d.worksheets || []);
    } catch (e) { toast.error(e?.message || 'Could not load worksheets'); setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    for (const key of ['class_level', 'subject', 'chapter']) {
      if (!scope[key].trim()) return toast.error(`${key.replace('_', ' ')} is needed`);
    }
    if (!files.length) return toast.error('Pick at least one worksheet file');
    setBusy(true);
    let done = 0;
    // One request per file so a single bad scan never loses the whole batch.
    for (const file of files) {
      setProgress(`Uploading ${done + 1} of ${files.length}…`);
      try {
        const fd = new FormData();
        fd.append('file', file);
        Object.entries(scope).forEach(([k, v]) => fd.append(k, v));
        fd.append('title', file.name.replace(/\.[^.]+$/, ''));
        const res = await authedFetch(`${API_BASE}/admin/worksheets`, { method: 'POST', body: fd });
        if (!res.ok) throw new Error(await res.text());
        done += 1;
      } catch (e) { toast.error(`${file.name}: ${e?.message || 'failed'}`); }
    }
    setProgress('');
    setBusy(false);
    setFiles([]);
    toast.success(`${done} of ${files.length} worksheet(s) published`);
    load();
  };

  const remove = async (id) => {
    try { await apiDelete(`/admin/worksheets/${id}`); toast.success('Removed'); load(); }
    catch (e) { toast.error(e?.message || 'Could not remove that worksheet'); }
  };

  return (
    <div className="space-y-5" data-testid="worksheets-bulk-panel">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
          <UploadCloud className="w-4 h-4 text-indigo-500" /> Publish worksheets against a chapter
        </h3>
        <p className="text-xs text-slate-500 font-body">
          Same flow as Bulk Infographics: set the chapter once, then drop in every worksheet for it.
          Students on that board and class see them straight away.
        </p>

        <div className="grid sm:grid-cols-4 gap-2.5 mt-3">
          {[['board', 'Board'], ['class_level', 'Class'], ['subject', 'Subject'], ['chapter', 'Chapter']].map(([key, label]) => (
            <label key={key} className="block">
              <span className="font-body text-[11px] text-slate-500">{label}</span>
              <Input value={scope[key]} onChange={e => setScope(s => ({ ...s, [key]: e.target.value }))}
                className="rounded-xl text-sm mt-0.5" data-testid={`worksheet-field-${key}`} />
            </label>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3">
          <div className="flex gap-1.5">
            {LEVELS.map(l => (
              <button key={l} onClick={() => setScope(s => ({ ...s, level: l }))}
                data-testid={`worksheet-level-${l}`}
                className={`text-[11px] font-heading px-2.5 py-1 rounded-full border ${scope.level === l ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200'}`}>
                {l}
              </button>
            ))}
          </div>
          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp"
            onChange={e => setFiles([...(e.target.files || [])])} data-testid="worksheet-files-input"
            className="text-[12px] font-body text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:text-indigo-700" />
          <Button onClick={upload} disabled={busy} className="rounded-xl" data-testid="worksheet-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {busy ? progress : `Publish ${files.length || ''} worksheet(s)`}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4 text-emerald-500" /> Published worksheets
        </h3>
        {!rows && <p className="mt-3 text-sm text-slate-400 font-body flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>}
        {rows?.length === 0 && <p className="mt-3 text-sm text-slate-500 font-body" data-testid="worksheets-empty">Nothing published yet.</p>}
        <div className="mt-3 space-y-1.5">
          {(rows || []).map(w => (
            <div key={w.worksheet_id} className="rounded-xl border border-slate-200 p-2.5 flex items-start justify-between gap-3"
              data-testid={`worksheet-row-${w.worksheet_id}`}>
              <div className="min-w-0">
                <a href={w.file_url} target="_blank" rel="noreferrer" className="font-heading text-[13px] text-indigo-700 hover:underline">
                  {w.title}
                </a>
                <p className="font-body text-[11px] text-slate-500">
                  {w.board?.toUpperCase()} · Class {w.class_level} · {w.subject} · {w.chapter} · {w.level}
                  {w.questions ? ` · ${w.questions} questions` : ''}{w.answer_key_included ? ' · answer key' : ''}
                </p>
              </div>
              <button onClick={() => remove(w.worksheet_id)} data-testid={`worksheet-remove-${w.worksheet_id}`}
                className="text-slate-400 hover:text-rose-600 flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ContentMgmtWorksheetsBulk;
